/**
 * WebSocket Monitor for Firebase Firestore
 * Helps diagnose WebSocket connection issues
 */

let originalWebSocket: typeof WebSocket | null = null;
let webSocketInstance: WebSocket | null = null;
let isMonitored = false;

/**
 * Start monitoring WebSocket connections for Firestore
 * This helps diagnose why real-time listeners aren't working
 */
export function monitorFirebaseWebSocket() {
  if (isMonitored) {
    console.log('✅ [WEBSOCKET] Already monitoring WebSocket connections');
    return;
  }

  try {
    // Only works in browser environment
    if (typeof window === 'undefined' || !window.WebSocket) {
      console.warn('⚠️ [WEBSOCKET] WebSocket not available (not in browser)');
      return;
    }

    // Store original WebSocket
    originalWebSocket = window.WebSocket;

    // Create wrapper to intercept WebSocket creation
    (window as any).WebSocket = function (url: string, protocols?: string | string[]) {
      console.log('🔌 [WEBSOCKET] Creating WebSocket connection');
      console.log('🔌 [WEBSOCKET] URL:', url);
      console.log('🔌 [WEBSOCKET] Protocols:', protocols);

      // Create the actual WebSocket
      const ws = new originalWebSocket!(url, protocols);
      webSocketInstance = ws;

      // Monitor connection lifecycle
      ws.addEventListener('open', () => {
        console.log('✅ [WEBSOCKET] Connection OPENED');
        console.log('✅ [WEBSOCKET] Real-time listeners should now receive updates');
        console.log('✅ [WEBSOCKET] Pending writes will now sync to backend');
      });

      ws.addEventListener('message', (event) => {
        console.log('📨 [WEBSOCKET] Message received:', event.data.substring(0, 100) + '...');
      });

      ws.addEventListener('error', (event) => {
        console.error('❌ [WEBSOCKET] Error occurred');
        console.error('❌ [WEBSOCKET] Error event:', event);
        console.error('❌ [WEBSOCKET] This may indicate network issues or Firebase misconfiguration');
      });

      ws.addEventListener('close', (event) => {
        console.warn('⚠️ [WEBSOCKET] Connection CLOSED');
        console.warn('⚠️ [WEBSOCKET] Close code:', event.code);
        console.warn('⚠️ [WEBSOCKET] Close reason:', event.reason);
        console.warn('⚠️ [WEBSOCKET] Real-time listeners will stop receiving updates');
        console.warn('⚠️ [WEBSOCKET] To reconnect, call `enableNetwork()` in firebase-web/config.ts');
      });

      return ws;
    };

    // Copy static properties and methods
    if (originalWebSocket) {
      (window.WebSocket as any).CONNECTING = originalWebSocket.CONNECTING;
      (window.WebSocket as any).OPEN = originalWebSocket.OPEN;
      (window.WebSocket as any).CLOSING = originalWebSocket.CLOSING;
      (window.WebSocket as any).CLOSED = originalWebSocket.CLOSED;
    }

    isMonitored = true;
    console.log('✅ [WEBSOCKET] WebSocket monitoring enabled');
    console.log('ℹ️ [WEBSOCKET] All WebSocket connections will be logged to console');
  } catch (error) {
    console.error('❌ [WEBSOCKET] Failed to set up monitoring:', error);
  }
}

/**
 * Get current WebSocket status
 */
export function getWebSocketStatus(): {
  monitored: boolean;
  connected: boolean;
  readyState: number | null;
  url: string | null;
} {
  return {
    monitored: isMonitored,
    connected: webSocketInstance?.readyState === 1, // 1 = OPEN
    readyState: webSocketInstance?.readyState ?? null,
    url: webSocketInstance?.url ?? null,
  };
}

/**
 * Get human-readable WebSocket ready state
 */
export function getReadyStateLabel(readyState: number | null): string {
  if (readyState === null) return 'NO_CONNECTION';
  switch (readyState) {
    case 0:
      return 'CONNECTING';
    case 1:
      return 'OPEN';
    case 2:
      return 'CLOSING';
    case 3:
      return 'CLOSED';
    default:
      return 'UNKNOWN';
  }
}

/**
 * Print WebSocket diagnostics to console
 */
export function printWebSocketDiagnostics() {
  const status = getWebSocketStatus();
  console.log('🔌 [WEBSOCKET] === DIAGNOSTICS ===');
  console.log('🔌 [WEBSOCKET] Monitoring enabled:', status.monitored);
  console.log(
    '🔌 [WEBSOCKET] Connection status:',
    status.connected ? '✅ CONNECTED' : '❌ NOT CONNECTED'
  );
  console.log('🔌 [WEBSOCKET] Ready state:', getReadyStateLabel(status.readyState));
  console.log('🔌 [WEBSOCKET] URL:', status.url || 'NO_URL');
  console.log('🔌 [WEBSOCKET] ==================');
}

/**
 * Manually close WebSocket for testing
 */
export function closeWebSocket() {
  if (webSocketInstance) {
    console.log('⚠️ [WEBSOCKET] Manually closing WebSocket for testing...');
    webSocketInstance.close();
    webSocketInstance = null;
    console.log('✅ [WEBSOCKET] WebSocket closed');
  }
}

/**
 * Expose WebSocket diagnostics globally for debugging
 */
export function exposeWebSocketGlobals() {
  if (typeof window !== 'undefined') {
    (window as any).__websocketMonitor = {
      getStatus: getWebSocketStatus,
      printDiagnostics: printWebSocketDiagnostics,
      closeWebSocket,
      getReadyStateLabel,
    };
    console.log('✅ [WEBSOCKET] Global helpers exposed: window.__websocketMonitor');
  }
}
